import { useEffect, useMemo, useRef } from "react";
import type { QueryKey } from "@tanstack/react-query";
import { useQueryClient } from "@tanstack/react-query";
import { API_BASE_URL } from "@/services/api";
import { useSocketStore } from "@/store/useSocketStore";

type CompetitorsRealtimeParams = {
  brandId?: string;
  status?: string;
  providers?: string[];
  enabled?: boolean;
  queryKey: QueryKey;
};

type CompetitorsSocketFilters = {
  brandId: string;
  status: string;
  providers?: string;
};

type CompetitorsSocketPayload = {
  brandId: string;
  filters: CompetitorsSocketFilters;
  competitors: any[];
};

type CompetitorsQueryCachePayload =
  | any[]
  | {
      competitors?: any[];
      competitorWidget?: unknown;
    };

const buildFiltersSignature = (filters: CompetitorsSocketFilters) =>
  `${filters.brandId}:${filters.status}:${filters.providers ?? ""}`;

const normalizeProviders = (providers?: string[]) => {
  if (!providers?.length) return undefined;
  const normalized = Array.from(
    new Set(
      providers
        .map((provider) => String(provider || "").trim().toUpperCase())
        .filter(Boolean),
    ),
  ).sort();
  return normalized.length ? normalized.join(",") : undefined;
};

export const useCompetitorsRealtime = ({
  brandId,
  status = "tracked",
  providers,
  enabled = true,
  queryKey,
}: CompetitorsRealtimeParams) => {
  const queryClient = useQueryClient();
  const latestQueryKeyRef = useRef<QueryKey>(queryKey);
  const ensureSocket = useSocketStore((state) => state.ensureSocket);
  const addCompetitorsSubscription = useSocketStore(
    (state) => state.addCompetitorsSubscription,
  );
  const removeCompetitorsSubscription = useSocketStore(
    (state) => state.removeCompetitorsSubscription,
  );

  useEffect(() => {
    latestQueryKeyRef.current = queryKey;
  }, [queryKey]);

  const filters = useMemo<CompetitorsSocketFilters | null>(() => {
    if (!brandId) return null;
    return {
      brandId,
      status,
      providers: normalizeProviders(providers),
    };
  }, [brandId, status, providers]);

  useEffect(() => {
    if (!enabled || !filters) {
      return;
    }
    const signature = buildFiltersSignature(filters);
    const socket = ensureSocket(API_BASE_URL);

    const subscribePayload = {
      brandId: filters.brandId,
      status: filters.status,
      providers: filters.providers,
    };

    const handleResults = (payload: CompetitorsSocketPayload) => {
      if (!payload?.filters) {
        return;
      }
      const payloadSignature = buildFiltersSignature(payload.filters);
      if (payloadSignature !== signature) {
        return;
      }
      if (!Array.isArray(payload.competitors)) {
        void queryClient.invalidateQueries({
          queryKey: latestQueryKeyRef.current,
          refetchType: "active",
        });
        return;
      }
      queryClient.setQueryData(
        latestQueryKeyRef.current,
        (currentData: CompetitorsQueryCachePayload | undefined) => {
          if (
            currentData &&
            typeof currentData === "object" &&
            !Array.isArray(currentData)
          ) {
            return {
              ...currentData,
              competitors: payload.competitors,
            };
          }
          return payload.competitors;
        },
      );
    };

    socket.on("competitors:results", handleResults);
    addCompetitorsSubscription(signature, subscribePayload);

    return () => {
      socket.off("competitors:results", handleResults);
      removeCompetitorsSubscription(signature);
    };
  }, [
    enabled,
    filters,
    queryClient,
    ensureSocket,
    addCompetitorsSubscription,
    removeCompetitorsSubscription,
  ]);
};
